import {
  buildCDNURL,
  getCurrentLang,
  getTitleMax,
  handleHtmlTags,
  toISO8601Duration,
  toISO8601WithOffset,
  trimString,
} from "@platformx/utilities";
import { buildContentUrl, omitEmpty } from "./common";
import { getPublisherNode } from "./publisher";
import { buildBrightcoveEmbedUrl } from "./videoPlayer";

export type VodStructureDataInput = {
  title?: string;
  description?: string;
  /**
   * DSpace/GCS bitstream URL of the media file itself. No longer emitted — see
   * the builder docblock — but still used to validate pre-existing markup that
   * carries a `contentUrl`.
   */
  videoUrl?: string;
  /** Brightcove video id, captured when the asset is picked from the DAM. */
  videoId?: string;
  /**
   * Video length in MILLISECONDS, the unit the Brightcove CMS API reports.
   * Supplied at DAM-pick time only — it is NOT a persisted VOD field, so on
   * every later save it arrives empty and `carriedOverVideoFields` reads it
   * back out of the stored markup. Empty until the DAM exposes
   * `dc.video.duration` (KIWI-6410).
   */
  durationMs?: number | string;
  /** `original_image` — the poster frame, as a relative path + extension. */
  originalImage?: any;
  /** Absolute DAM thumbnail URL, used when no relative path is stored. */
  thumbnail?: string;
  /** Slug (preferred) or page id used in the delivery route. */
  pageUrl?: string;
  selectedBrandRoute?: string;
  uploadDate?: string;
  language?: string;
  /**
   * The VOD's previously stored markup. Read ONLY to carry `embedUrl` and
   * `duration` across a rebuild that cannot produce them — see
   * `carriedOverVideoFields`.
   */
  storedStructureData?: string | Record<string, any>;
};

const BRIGHTCOVE_PLAYER_ORIGIN = "https://players.brightcove.net/";

/** Parses stored markup, unwrapping the KIWI-6278 `{ use, auto, manual }` envelope. */
const parseStoredMarkup = (stored: string | Record<string, any> | undefined): any => {
  if (!stored) return null;

  let parsed: any = stored;
  if (typeof stored === "string") {
    try {
      parsed = JSON.parse(stored);
    } catch {
      return null;
    }
  }
  if (parsed && typeof parsed === "object" && (parsed.use === "auto" || parsed.use === "manual")) {
    parsed = parsed.auto;
  }
  return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
};

/** The Brightcove id a stored player URL points at, or `""` if it is not one. */
const videoIdFromEmbedUrl = (embedUrl: any): string => {
  if (typeof embedUrl !== "string" || !embedUrl.startsWith(BRIGHTCOVE_PLAYER_ORIGIN)) return "";
  return embedUrl.match(/[?&]videoId=([^&]+)/)?.[1] || "";
};

/**
 * Carries `embedUrl` and `duration` across a rebuild that cannot produce them.
 *
 * KIWI-6283: the authoring backend does not return `ObjectFields.VodVideoId` on
 * load, so a re-opened VOD arrives with no Brightcove id. Because the VOD save
 * path rebuilds `structure_data` unconditionally, the correct `embedUrl` was
 * SILENTLY DROPPED by the next save — an author editing only the title lost it.
 * `duration` has no persisted field at all — the authoring backend whitelists
 * `ObjectFields` and rejects a save outright on an unknown key — so the markup
 * IS its storage and this carry-over is the only thing keeping it. Do not
 * remove the duration half until `VodDuration` is both accepted and returned.
 *
 * Identity of the asset is established as follows:
 * - A live `videoId` means the author picked a video, so only markup pointing at
 *   that same Brightcove id may contribute. The DAM picker refuses a video that
 *   is not `PLAYABLE`, and `PLAYABLE` implies a Brightcove id exists, so a swap
 *   always supplies one.
 * - No live `videoId` means nothing was picked in this session and the stored
 *   markup still describes this VOD. Markup written before `contentUrl` was
 *   dropped is additionally checked against the media file URL.
 */
const carriedOverVideoFields = (
  stored: string | Record<string, any> | undefined,
  videoId: string,
  videoUrl: string,
): { embedUrl?: string; duration?: string } => {
  const parsed = parseStoredMarkup(stored);
  if (!parsed) return {};

  const {
    embedUrl: storedEmbedUrl,
    duration: storedDuration,
    contentUrl: storedContentUrl,
  } = parsed;
  const storedDurationValue = typeof storedDuration === "string" ? storedDuration : "";

  if (videoId) {
    // Same video re-picked: its duration is still valid. `embedUrl` is rebuilt
    // from the live id, so it is never carried here.
    return videoIdFromEmbedUrl(storedEmbedUrl) === videoId ? { duration: storedDurationValue } : {};
  }

  // Markup written before contentUrl was dropped still names its media file —
  // when that disagrees, the author swapped the video and nothing may be reused.
  if (storedContentUrl && storedContentUrl !== videoUrl) return {};

  return {
    // Only a real player URL. Pre-KIWI-6283 markup set embedUrl to the DSpace
    // URL, and carrying that over would reintroduce the very bug this fixes.
    embedUrl: videoIdFromEmbedUrl(storedEmbedUrl) ? storedEmbedUrl : "",
    duration: storedDurationValue,
  };
};

/**
 * `VideoObject` markup for a VOD (KIWI-6283).
 *
 * Fixes what the SEO audit raised:
 * - drops the `@graph` wrapper — the VideoObject is the root node
 * - `thumbnailUrl` is an ARRAY of URL strings, not `{ "@id": … }`
 * - `embedUrl` is the Brightcove player page
 * - adds `uploadDate`, `url`, `duration`, `inLanguage`, `isFamilyFriendly` and
 *   `publisher`
 *
 * `contentUrl` is deliberately NOT emitted (requested 2026-09-14 on KIWI-6280):
 * the DSpace bitstream URL is not a crawlable media file, so `embedUrl` is the
 * only pointer to the video.
 *
 * `duration` is supplied in milliseconds when the author picks the video and is
 * omitted while the DAM has no value — it does not expose Brightcove's duration
 * until KIWI-6410 maps it to `dc.video.duration`. Google treats it as
 * recommended, not required, so the markup is valid either way.
 */
export const buildVodStructureData = ({
  title = "",
  description = "",
  videoUrl = "",
  videoId = "",
  durationMs = "",
  originalImage = {},
  thumbnail = "",
  pageUrl = "",
  selectedBrandRoute = "en",
  uploadDate = "",
  language = "",
  storedStructureData,
}: VodStructureDataInput = {}) => {
  const { original_image_relative_path: relativePath = "", ext = "" } = originalImage || {};
  const thumbnailUrl = relativePath
    ? buildCDNURL(relativePath, ext)
    : originalImage?.Thumbnail || thumbnail || "";
  const carried = carriedOverVideoFields(storedStructureData, videoId, videoUrl);

  return omitEmpty({
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: trimString(handleHtmlTags(title), getTitleMax("vod")),
    description: handleHtmlTags(description),
    thumbnailUrl: thumbnailUrl ? [thumbnailUrl] : [],
    uploadDate: toISO8601WithOffset(uploadDate || new Date().toISOString()),
    duration: toISO8601Duration(durationMs) || carried.duration,
    url: buildContentUrl("vod", pageUrl, selectedBrandRoute),
    embedUrl: buildBrightcoveEmbedUrl(videoId) || carried.embedUrl,
    inLanguage: language || getCurrentLang(),
    isFamilyFriendly: true,
    publisher: getPublisherNode(),
  });
};
